import { useApolloClient } from '@apollo/client'
import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { GetProjectElementNodesForAEProjectWSQuery } from 'api/graphQL/queries/projectElements.queries'
import { useActionBarStore } from 'components/WorkspaceEditor/ActionBarStore'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { useCallback } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { usePTActivationStore } from '../store'

// Upper bound for Z4010 nodes per section code. A typical MasterSpec section has
// ~10–50 product types; 1000 gives a 20× safety margin while keeping the response payload small.
// If a section legitimately exceeds this, the Family Groups view will show stale nodes —
// raise this value and re-test if that occurs.
const FAMILY_GROUP_QUERY_LIMIT = 1000

interface FamilyGroupQueryResponse {
    getProjectElementNodes?: {
        projectElementNodes?: ProjectElementNode[]
    }
}

/**
 * Hook that fetches fresh Family Groups nodes after PT activation and syncs them to the store.
 * Only runs in assembly view — division view has its own rendering path.
 */
export const useFamilyGroupSync = () => {
    const client = useApolloClient()
    const projectId = useWorkspace2Store((state) => state.projectId)
    const selectedElementFormat = useElementItemStore(
        (state) => state.selectedElementFormat
    )
    const elementItemValue = useElementItemStore(
        (state) => state.elementItemValue
    )
    const setUpdatedFamilyGroupNodes = usePTActivationStore(
        (state) => state.setUpdatedFamilyGroupNodes
    )

    const syncFamilyGroupNodes = useCallback(
        async (ptElementNodes: ProjectElementNode[]): Promise<void> => {
            if (selectedElementFormat === 'DIVISIONS' || !projectId) {
                return
            }

            // Condition 1: current family node is under the Z4010 (Family Groups) assembly
            // PT nodes' parentNodeId points to their family — if any PT belongs to current family and has Z4010 treePath, we're in Family Groups
            const isInFamilyGroups = ptElementNodes.some(
                (node) =>
                    node.elementType === ProjectElementType.PRODUCTTYPE &&
                    node.parentNodeId === elementItemValue?.id &&
                    node.treePath?.startsWith('Z4010')
            )
            if (isInFamilyGroups) return

            // Condition 2: every activated PT already has its Z4010 counterpart in project
            // elementList contains Z4010 nodes even in assembly view — check directly
            const elementList = useActionBarStore.getState().elementList || []
            const z4010AlreadyInProject = ptElementNodes.every((activatedPT) =>
                elementList.some(
                    (el) =>
                        el.elementType === ProjectElementType.PRODUCTTYPE &&
                        el.treePath?.startsWith('Z4010') &&
                        el.baseElementId === activatedPT.baseElementId &&
                        el.isInProject
                )
            )
            if (z4010AlreadyInProject) return

            try {
                // Extract family number from PT treePath: second-to-last '||' segment's first '|' part
                // e.g. "A2010|...|A2010.1020|CMU||076200|SHEET METAL FLASHING AND TRIM||Copper Sheet" → "076200"
                const sectionCodes = new Set<string>(
                    ptElementNodes
                        .map((pt) => {
                            const segments = (pt.treePath || '').split('||')
                            const familySegment = segments[segments.length - 2]
                            return familySegment?.split('|')[0] || ''
                        })
                        .filter(Boolean)
                )

                if (sectionCodes.size === 0) return

                const z4010Nodes: ProjectElementNode[] = []
                for (const sectionCode of sectionCodes) {
                    const { data } = await client.query<
                        FamilyGroupQueryResponse
                    >({
                        query: GetProjectElementNodesForAEProjectWSQuery,
                        variables: {
                            input: {
                                projectId,
                                limit: FAMILY_GROUP_QUERY_LIMIT,
                                skip: 0,
                                onlyElementsAssignedToProject: true,
                                projectElementType: [
                                    ProjectElementType.ASSEMBLY,
                                    ProjectElementType.PRODUCTFAMILY,
                                    ProjectElementType.PRODUCTTYPE,
                                ],
                                parentNodeId: 'root',
                                isProductFamilyGroupView: false,
                                isFromAEWorkSpace: true,
                                skipChildNodeChecks: true,
                                searchFilters: [
                                    {
                                        projectElementType:
                                            ProjectElementType.ASSEMBLY,
                                        searchText: sectionCode,
                                    },
                                    {
                                        projectElementType:
                                            ProjectElementType.PRODUCTFAMILY,
                                        searchText: sectionCode,
                                    },
                                    {
                                        projectElementType:
                                            ProjectElementType.PRODUCTTYPE,
                                        searchText: sectionCode,
                                    },
                                ],
                            },
                        },
                        fetchPolicy: 'network-only',
                    })
                    const nodes =
                        data?.getProjectElementNodes?.projectElementNodes || []
                    z4010Nodes.push(...nodes)
                }

                if (z4010Nodes.length > 0) {
                    setUpdatedFamilyGroupNodes(z4010Nodes)
                }
            } catch (err) {
                console.error('[syncFamilyGroupNodes] error:', err)
            }
        },
        [client, projectId, selectedElementFormat, elementItemValue, setUpdatedFamilyGroupNodes]
    )

    return { syncFamilyGroupNodes }
}
